import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, ExternalLink, Copy, MapPin, Package, Pencil, DollarSign, Boxes, Check, X } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function PaperAdminLayoutListPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProjects = () => {
    setLoading(true);
    axios.get(`${API}/api/event-layout/projects`, { headers })
      .then(res => setProjects(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) return alert('제목을 입력해주세요.');
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/event-layout/projects`, form, { headers });
      navigate(`${PAPER_BASE}/layouts/${res.data.id}/edit`);
    } catch {
      alert('생성 중 오류가 발생했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`"${title}" 행사를 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API}/api/event-layout/projects/${id}`, { headers });
      fetchProjects();
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const startEditing = (p) => {
    setEditingId(p.id);
    setEditForm({ title: p.title, description: p.description || '' });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ title: '', description: '' });
  };

  const handleUpdate = async (id) => {
    if (!editForm.title.trim()) return alert('제목을 입력해주세요.');
    try {
      await axios.put(`${API}/api/event-layout/projects/${id}`, editForm, { headers });
      setProjects(prev => prev.map(p => p.id === id ? { ...p, ...editForm } : p));
      setEditingId(null);
    } catch {
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  const handleDuplicate = async (id, title) => {
    if (!window.confirm(`"${title}" 행사를 복제하시겠습니까?`)) return;
    try {
      await axios.post(`${API}/api/event-layout/projects/${id}/duplicate`, {}, { headers });
      fetchProjects();
    } catch {
      alert('복제 중 오류가 발생했습니다.');
    }
  };

  const copyLink = (shareId, editable) => {
    const base = window.location.origin;
    const url = editable
      ? `${base}${PAPER_BASE}/layouts/share/${shareId}?edit=1`
      : `${base}${PAPER_BASE}/layout/${shareId}`;
    const msg = editable ? '편집 가능 링크가 복사되었습니다.' : '보기용 링크가 복사되었습니다.';
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => alert(msg));
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(msg);
    }
  };

  return (
    <div className="bg-[#0d0b14] min-h-screen">
      {/* Title Section */}
      <section className="pt-10 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-white text-2xl font-bold">행사관리</h1>
              {projects.length > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400">
                  전체 {projects.length}건
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Link to={`${PAPER_BASE}/layouts/assets`} className="flex items-center gap-2 px-4 py-2 text-sm text-white/60 hover:text-white border border-white/10 rounded-lg transition-colors">
                <Package size={16} /> 에셋 관리
              </Link>
              <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-5 py-2.5 bg-violet-500 hover:bg-violet-600 text-white text-sm rounded-lg transition-colors">
                <Plus size={16} /> 새 행사
              </button>
            </div>
          </div>
          <p className="text-slate-500 text-sm">행사별 배치도, 예산, 품목을 통합 관리하세요</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">

      {/* 생성 모달 */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-[#1a1428] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-medium text-white mb-4">새 행사</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-1">제목</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400"
                  placeholder="예: 2026 봄꽃축제" autoFocus />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">설명 (선택)</label>
                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400"
                  placeholder="메모" />
              </div>
              <button type="submit" disabled={creating}
                className="w-full py-2.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
                {creating ? '생성 중...' : '생성 후 편집하기'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 프로젝트 목록 */}
      {loading ? (
        <div className="text-center text-white/40 py-20">로딩 중...</div>
      ) : projects.length === 0 ? (
        <div className="text-center text-white/40 py-20">
          <MapPin size={48} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg mb-2">등록된 행사가 없습니다</p>
          <p className="text-sm">새 행사를 추가하여 배치도, 예산, 품목을 관리하세요</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => (
            <div key={p.id} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-violet-400/30 transition-colors group">
              <div className="flex items-start justify-between mb-3">
                {editingId === p.id ? (
                  <div className="flex-1 min-w-0 space-y-2">
                    <input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdate(p.id); if (e.key === 'Escape') cancelEditing(); }}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-sm outline-none focus:border-violet-400"
                      autoFocus />
                    <input type="text" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdate(p.id); if (e.key === 'Escape') cancelEditing(); }}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white/60 text-xs outline-none focus:border-violet-400"
                      placeholder="설명" />
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleUpdate(p.id)} className="text-emerald-400 hover:text-emerald-300 transition-colors">
                        <Check size={15} />
                      </button>
                      <button onClick={cancelEditing} className="text-white/30 hover:text-white/60 transition-colors">
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{p.title}</h3>
                      {p.description && <p className="text-white/40 text-sm mt-1 truncate">{p.description}</p>}
                      {p.work && <p className="text-violet-400/60 text-xs mt-1">행사: {p.work.title}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100">
                      <button onClick={() => startEditing(p)} title="수정"
                        className="text-white/20 hover:text-violet-400 transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDuplicate(p.id, p.title)} title="복제"
                        className="text-white/20 hover:text-violet-400 transition-colors">
                        <Copy size={15} />
                      </button>
                      <button onClick={() => handleDelete(p.id, p.title)} title="삭제"
                        className="text-white/20 hover:text-red-400 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </>
                )}
              </div>

              <p className="text-white/30 text-xs mb-4">{new Date(p.createdAt).toLocaleDateString('ko-KR')}</p>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Link to={`${PAPER_BASE}/layouts/${p.id}/edit`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 text-sm rounded-lg transition-colors">
                    <ExternalLink size={14} /> 배치도편집
                  </Link>
                  <button onClick={() => copyLink(p.shareId, false)} title="보기용 링크 복사"
                    className="p-2 bg-white/5 hover:bg-white/10 text-white/40 hover:text-white rounded-lg transition-colors">
                    <Copy size={14} />
                  </button>
                  <button onClick={() => copyLink(p.shareId, true)} title="편집 가능 링크 복사"
                    className="p-2 bg-white/5 hover:bg-white/10 text-violet-400/40 hover:text-violet-400 rounded-lg transition-colors">
                    <Pencil size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`${PAPER_BASE}/layouts/${p.id}/budget`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 text-sm rounded-lg transition-colors">
                    <DollarSign size={14} /> 예산관리
                  </Link>
                  <Link to={`${PAPER_BASE}/layouts/${p.id}/items`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-sky-500/15 hover:bg-sky-500/25 text-sky-400 text-sm rounded-lg transition-colors">
                    <Boxes size={14} /> 렌탈관리
                  </Link>
                  <Link to={`${PAPER_BASE}/layouts/${p.id}/inventory`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 text-sm rounded-lg transition-colors">
                    <Package size={14} /> 물품관리
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
